import React, { useEffect, useState } from 'react';
import { bool, object } from 'prop-types';
import { connect } from 'react-redux';

import { LayoutSingleColumn, Page } from '../../components';
import TopbarContainer from '../TopbarContainer/TopbarContainer';
import FooterContainer from '../FooterContainer/FooterContainer';
import SeoIndexAlphabetNav from '../../components/SeoDiscovery/SeoIndexAlphabetNav';
import { propTypes } from '../../util/types';
import css from '../../components/SeoDiscovery/SeoDiscovery.module.css';
import { fetchSeoCitiesIndex } from '../../util/seoDiscoveryFetch';
import { INDEX_LETTERS, citiesLetterPath } from '../../util/seoDiscoveryIndexLetters';

const cityLabel = city => [city.city, city.state].filter(Boolean).join(', ');

export const SeoDiscoveryCitiesIndexPageComponent = props => {
  const { data, fetchInProgress, fetchError } = props;
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState(null);
  const [searchInProgress, setSearchInProgress] = useState(false);
  const letterCounts = data?.letterCounts || {};
  const normalizedQuery = searchQuery.trim();
  const hasSearch = normalizedQuery.length > 0;
  const cities = hasSearch ? searchResults?.cities || [] : [];
  const total = hasSearch ? searchResults?.total || 0 : 0;

  useEffect(() => {
    if (!hasSearch) {
      setSearchResults(null);
      return undefined;
    }
    setSearchInProgress(true);
    const handle = window.setTimeout(() => {
      fetchSeoCitiesIndex({
        q: normalizedQuery,
        limit: '500',
      })
        .then(payload => setSearchResults(payload))
        .catch(() => setSearchResults({ cities: [], total: 0 }))
        .finally(() => setSearchInProgress(false));
    }, 300);
    return () => window.clearTimeout(handle);
  }, [hasSearch, normalizedQuery]);

  return (
    <Page
      title="Cities | Event Discovery | PromoTix"
      description="Browse cities on PromoTix. Find upcoming events, tickets, and venue guides by city."
      schema={{
        '@context': 'https://schema.org',
        '@type': 'CollectionPage',
        name: 'PromoTix Cities',
      }}
    >
      <LayoutSingleColumn topbar={<TopbarContainer />} footer={<FooterContainer />}>
        <div className={css.root}>
          <h1 className={css.h1}>Cities</h1>
          <p>Discover upcoming events and venue guides by city.</p>
          {fetchError ? <p>Unable to load cities.</p> : null}
          {fetchInProgress && !Object.keys(letterCounts).length ? <p>Loading…</p> : null}
          <div className={css.venueSearch}>
            <label className={css.venueSearchLabel} htmlFor="city-index-search">
              Find a specific city
            </label>
            <div className={css.venueSearchControl}>
              <input
                id="city-index-search"
                className={css.venueSearchInput}
                type="search"
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                placeholder="Search by city or state"
                autoComplete="off"
              />
              {hasSearch ? (
                <button
                  className={css.venueSearchClear}
                  type="button"
                  onClick={() => setSearchQuery('')}
                >
                  Clear
                </button>
              ) : null}
            </div>
            {hasSearch ? (
              <p className={css.venueSearchHelp} aria-live="polite">
                {searchInProgress
                  ? 'Searching…'
                  : `${total} cit${total === 1 ? 'y' : 'ies'} found`}
              </p>
            ) : null}
          </div>
          {!hasSearch ? (
            <>
              <SeoIndexAlphabetNav letterCounts={letterCounts} getLetterHref={citiesLetterPath} />
              <ul className={css.indexHubLetters}>
                {INDEX_LETTERS.filter(letter => (letterCounts[letter] || 0) > 0).map(letter => (
                  <li key={letter}>
                    <a href={citiesLetterPath(letter)}>
                      {letter === '#' ? '0–9' : letter} — {letterCounts[letter]} cit
                      {letterCounts[letter] === 1 ? 'y' : 'ies'}
                    </a>
                  </li>
                ))}
              </ul>
            </>
          ) : null}
          {hasSearch && cities.length > 0 ? (
            <section className={css.letterSection}>
              <h2 className={css.letterHeading}>Search results</h2>
              <ul className={css.indexList}>
                {cities.map(c => (
                  <li key={c.slug}>
                    <a href={c.path}>{cityLabel(c)}</a>
                    <span className={css.eventMeta}>
                      {' '}
                      — {c.eventCount || 0} event{(c.eventCount || 0) === 1 ? '' : 's'},{' '}
                      {c.venueCount || 0} venue{(c.venueCount || 0) === 1 ? '' : 's'}
                    </span>
                  </li>
                ))}
              </ul>
            </section>
          ) : null}
          {hasSearch && !searchInProgress && total === 0 ? (
            <p className={css.venueSearchNoResults}>
              No cities found for "{normalizedQuery}". Try a city or state name.
            </p>
          ) : null}
        </div>
      </LayoutSingleColumn>
    </Page>
  );
};

SeoDiscoveryCitiesIndexPageComponent.propTypes = {
  data: object,
  fetchInProgress: bool,
  fetchError: propTypes.error,
};

const mapStateToProps = state => state.SeoDiscoveryCitiesIndexPage || {};

export default connect(mapStateToProps)(SeoDiscoveryCitiesIndexPageComponent);
